import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Sparkles, Trash2, Rocket, Bell } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatErr, imgSrc } from "@/lib/api";
import { uploadImage } from "@/lib/imageUpload";

export function AnnouncementImagePicker({ value, onChange }) {
  const [busy, setBusy] = useState("");
  const [prompt, setPrompt] = useState("");
  const file = useRef(null);
  useEffect(() => { if (!value) setPrompt(""); }, [value]);
  const go = async (kind, fn) => {
    setBusy(kind);
    try { onChange((await fn()).url); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) go("upload", () => uploadImage("/feed/upload", f)); };
  const ai = () => go("ai", async () => (await api.post("/admin/announcements/ai-image", { prompt })).data);
  return (
    <div className="space-y-2 rounded-2xl border border-rose-100 p-3" data-testid="admin-ann-image-picker">
      <p className="text-sm font-semibold">Bild (optional)</p>
      {value && (
        <div className="relative">
          <img src={imgSrc(value)} alt="" className="w-full aspect-[3/2] object-cover rounded-xl" data-testid="admin-ann-image-preview" />
          <button onClick={() => onChange("")} data-testid="admin-ann-image-remove" className="absolute top-2 right-2 p-2 rounded-full bg-white/90 text-red-600 shadow"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      <input ref={file} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="admin-ann-image-file" />
      <button onClick={() => file.current?.click()} disabled={!!busy} data-testid="admin-ann-image-upload" className="w-full h-10 rounded-full border-2 border-rose-200 text-rose-600 text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-rose-50 disabled:opacity-50">
        {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Foto hochladen
      </button>
      <div className="flex gap-2">
        <Input data-testid="admin-ann-ai-prompt" className="rounded-xl" maxLength={500} placeholder="oder per KI: z. B. rosa Handy mit Herzen" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <button onClick={ai} disabled={!!busy || prompt.trim().length < 3} data-testid="admin-ann-ai-generate" className="shrink-0 px-4 h-10 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Erstellen
        </button>
      </div>
      {busy === "ai" && <p className="text-xs text-slate-500">Die KI malt dein Bild … das dauert bis zu 30 Sekunden.</p>}
    </div>
  );
}

export function StoreLaunchAdmin() {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ google_url: "", apple_url: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/store-waitlist").then(({ data }) => { setS(data); if (data.launch) setF({ google_url: data.launch.google_url, apple_url: data.launch.apple_url }); });
  useEffect(() => { load(); }, []);
  const ok = (u) => !u || u.startsWith("https://");
  const launch = async () => {
    if (!window.confirm(`Store-Start jetzt an ${s?.count || 0} Mitglieder auf der Warteliste melden?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/store-launch", f); toast.success(data.notified ? `Gemeldet · Push an ${data.notified} Mitglieder wird verschickt` : "Store-Links aktualisiert – alle auf der Warteliste wurden schon benachrichtigt"); load(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="admin-store-launch">
      <h3 className="font-bold text-lg flex items-center gap-2"><Rocket className="w-5 h-5 text-rose-500" /> Store-Start</h3>
      <p className="text-sm flex items-center gap-2" data-testid="admin-store-waitlist-count"><Bell className="w-4 h-4 text-rose-500" /> <b>{s?.count ?? "…"}</b> Mitglieder auf der Liste auf „Benachrichtige mich“</p>
      {s?.launch && <p className="text-xs text-emerald-600 font-semibold" data-testid="admin-store-launched">Bereits gemeldet am {new Date(s.launch.launched_at).toLocaleString("de-DE")} – Ankündigungen zeigen jetzt die Store-Links.</p>}
      <Input data-testid="admin-store-google" className="rounded-xl" placeholder="Google Play Link (https://…)" value={f.google_url} onChange={(e) => setF({ ...f, google_url: e.target.value.trim() })} />
      <Input data-testid="admin-store-apple" className="rounded-xl" placeholder="Apple App Store Link (https://…)" value={f.apple_url} onChange={(e) => setF({ ...f, apple_url: e.target.value.trim() })} />
      {(!ok(f.google_url) || !ok(f.apple_url)) && <p className="text-xs text-red-600">Links müssen mit https:// beginnen</p>}
      <button onClick={launch} disabled={busy || (!f.google_url && !f.apple_url) || !ok(f.google_url) || !ok(f.apple_url)} data-testid="admin-store-launch-btn" className="w-full h-11 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Store-Start jetzt melden
      </button>
    </div>
  );
}
